import { Row } from "@amzn/stencil-react-components/layout";
import { Table, TableColumn, TableSpacing } from "@amzn/stencil-react-components/table";
import { Text } from "@amzn/stencil-react-components/text";
import IconCheckCircleFill from "@amzn/stencil-react-icons/icons/icon-check-circle-fill";
import IconClockFill from "@amzn/stencil-react-icons/icons/icon-clock-fill";
import IconAlertTriangleFill from "@amzn/stencil-react-icons/icons/icon-alert-triangle-fill";
import IconCloseCircleFill from "@amzn/stencil-react-icons/icons/icon-close-circle-fill";
import React, { FC } from "react";
import { Link } from "react-router-dom";
import { Order, ORDER_SOURCE_IDS } from "@/services/orders/orders.types";
import { Paths, buildPath } from "@/common/paths/paths";
import { getStatusTone, getDeliveryMethodLabel } from "./hardware-orders-table.util";
import {
  OrdersLoadingView,
  OrdersErrorState,
  OrdersEmptyState,
  buildStatusLabel,
  buildDateComponent,
  messagesColumn,
} from "./hardware-orders-table.shared";

function buildOrderStatusComponent(value: string) {
  const tone = getStatusTone(value);
  const iconComponent =
    tone === "done" ? (
      <IconCheckCircleFill color="green70" />
    ) : tone === "alert" ? (
      <IconAlertTriangleFill color="red70" />
    ) : tone === "cancelled" ? (
      <IconCloseCircleFill color="neutral70" />
    ) : (
      <IconClockFill color="pink70" />
    );
  return (
    <Row gridGap={8}>
      {iconComponent} {buildStatusLabel(value)}
    </Row>
  );
}

function buildStatusCell(order: Order) {
  const content = buildOrderStatusComponent(order.status ?? "-");
  const serialNumber = order.replacedSerialNumber;
  if (order.source === ORDER_SOURCE_IDS.lcr && serialNumber) {
    return <Link to={buildPath(Paths.lcrOrderTracking.url, { serialNumber })}>{content}</Link>;
  }
  return content;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const baseColumns: TableColumn<any>[] = [
  {
    header: "Item",
    accessor: ({ data }: { data: Order }) => (
      <Text fontSize="T200" color="neutral90" fontWeight={600}>
        {data.items?.[0]?.itemInformation?.name ?? "-"}
      </Text>
    ),
  },
  {
    header: "Order",
    accessor: ({ data }: { data: Order }) => data.orderReference ?? "-",
  },
  {
    header: "Ordered",
    accessor: ({ data }: { data: Order }) => buildDateComponent(data.requested?.on),
  },
  {
    header: "Delivery method",
    accessor: ({ data }: { data: Order }) => getDeliveryMethodLabel(data.deliveryMethod),
  },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const statusColumn: TableColumn<any> = {
  header: "Status",
  accessor: ({ data }: { data: Order }) => buildStatusCell(data),
};

const tableColumns = [...baseColumns, messagesColumn, statusColumn];

interface HardwareOrdersTableProps {
  orders: Order[];
  isLoading: boolean;
  hasError?: boolean;
  onRetry?: () => void;
}

export const HardwareOrdersTable: FC<HardwareOrdersTableProps> = ({ orders, isLoading, hasError = false, onRetry }) => {
  if (isLoading) {
    return <OrdersLoadingView />;
  }

  if (hasError) {
    return <OrdersErrorState onRetry={onRetry} />;
  }

  if (orders.length === 0) {
    return <OrdersEmptyState />;
  }

  return <Table columns={tableColumns} data={orders} spacing={TableSpacing.Default} />;
};

export default HardwareOrdersTable;
